import * as React from "react";
import { toast } from "sonner";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { newIdempotencyKey } from "@/lib/api";
import { errorText, Spinner } from "./common";

/**
 * Onaylı işlem penceresi: kararlar (onay, ret, iade…) bir kez ve tekrar korumasıyla gönderilir.
 * Pencere açıldığında tek bir Idempotency-Key üretilir; çift tıklama ya da yeniden deneme aynı işlem sayılır.
 */
export function ActionDialog({
  trigger,
  title,
  description,
  confirmLabel = "Onayla",
  confirmVariant = "default",
  children,
  canSubmit = true,
  onSubmit,
  success,
  onOpen,
  wide = false,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: {
  /** yoksa pencere dışarıdan açılır (open/onOpenChange; ör. menü öğesinden) */
  trigger?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  confirmVariant?: ButtonProps["variant"];
  children?: React.ReactNode;
  canSubmit?: boolean;
  onSubmit: (idempotencyKey: string) => Promise<unknown>;
  /** başarı bildirimi; sonuca göre değişebilir (ör. "onaya gönderildi") */
  success: string | ((result: unknown) => string);
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** pencere her açıldığında (form alanlarını kayıttaki değerlere döndürmek için) */
  onOpen?: () => void;
  /** uzun formlar: geniş ve kaydırılabilir */
  wide?: boolean;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const setOpen = (o: boolean) => (controlled ? onOpenChangeProp?.(o) : setInternalOpen(o));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const key = React.useRef(newIdempotencyKey());

  // her açılışta yeni anahtar ve temiz form (içeriden ya da dışarıdan açılsın)
  React.useEffect(() => {
    if (!open) return;
    key.current = newIdempotencyKey();
    setError(null);
    onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onOpenChange = (o: boolean) => {
    if (busy) return;
    setOpen(o);
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await onSubmit(key.current);
      toast.success(typeof success === "function" ? success(result) : success);
      setOpen(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className={wide ? "max-h-[90vh] max-w-2xl overflow-y-auto" : undefined}>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children}
          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Vazgeç
            </Button>
            <Button type="submit" variant={confirmVariant} disabled={busy || !canSubmit}>
              {busy && <Spinner />}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
